import { useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminProvider from '../providers/AdminProvider'; // Import the new AdminProvider
import { useTranslation } from 'react-i18next';
import './AuthContainer.css'; // Assuming you have an AuthContainer.css for styling

const AuthContainer = () => {
    const { t, i18n } = useTranslation();
    const { state } = useLocation();

    // Retrieve admin token directly from localStorage
    const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
    // Note: setAdminToken is not explicitly called here because adminToken is
    // expected to be set once by AuthLogin and then read from localStorage on mount.

    // Data for display and coupon creation
    const [games, setGames] = useState([]);
    const [users, setUsers] = useState([]);
    const [surveys, setSurveys] = useState([]); // New state for surveys

    // Coupon Creation Form State
    const [selectedGame, setSelectedGame] = useState('');
    const [selectedUser, setSelectedUser] = useState(''); // Can be null if coupon is not assigned to a specific user
    const [couponMessage, setCouponMessage] = useState({ type: '', text: '' }); // 'success' | 'error'

    const [isLoading, setIsLoading] = useState(false);

    // Existing states for generic table management (keeping for now, but not directly used for coupon creation)
    const [selectedTable, setSelectedTable] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [updateData, setUpdateData] = useState('');

    // Fetch games and users on component mount
    useEffect(() => {
        const adminProvider = new AdminProvider();
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                // Fetch all games
                const fetchedGames = await adminProvider.getAllGames(i18n.language);
                setGames(fetchedGames);
                if (fetchedGames.length > 0) {
                    setSelectedGame(fetchedGames[0].id); // Select the first game by default
                }

                // Fetch all users
                const fetchedUsers = await adminProvider.getAllUsers();
                setUsers(fetchedUsers);

                 // Fetch all surveys
                 const fetchedSurveys = await adminProvider.getAllSurveys(i18n.language);
                 setSurveys(fetchedSurveys);
               
                // We don't pre-select a user, as it's optional for coupons
            } catch (error) {
                console.error('Error fetching initial admin data:', error);
                setCouponMessage({ type: 'error', text: t('admin_error_fetching_data', { defaultValue: 'Error fetching initial data for admin panel.' }) });
            } finally {
                setIsLoading(false);
            }
        };

        if (adminToken) { // Only fetch if an admin token exists
            fetchInitialData();
        }
    }, [adminToken, i18n.language, t]);

    const handleCreateCoupon = async () => {
        setCouponMessage({ type: '', text: '' }); // Clear previous messages
        setIsLoading(true);

        // Basic client-side validation
        if (!selectedGame || !selectedUser) {
            setCouponMessage({ type: 'error', text: t('admin_coupon_missing_fields', { defaultValue: 'Please fill in all required coupon fields.' }) });
            setIsLoading(false);
            return;
        }

        const couponPayload = {
            gameId: selectedGame,
            userId: selectedUser 
        };

        const adminProvider = new AdminProvider();
        try {
            const response = await adminProvider.createCoupon(couponPayload);
            setCouponMessage({ type: 'success', text: response.message || t('admin_coupon_created_success', { defaultValue: 'Coupon created successfully!' }) });
            setSelectedUser(''); // Clear selected user
        } catch (error) {
            console.error('Error creating coupon:', error);
            setCouponMessage({ type: 'error', text: error.message || t('admin_coupon_created_error', { defaultValue: 'Error creating coupon.' }) });
        } finally {
            setIsLoading(false);
        }
    };

    // Keeping existing table functions but adjusting for admin token if they are to be used.
    const handleTableClick = async (tableName) => {
        try {
            // Ensure adminToken is passed for authenticated API calls
            const response = await axios.get(`/api/${tableName}`, {
                headers: {
                    'x-admin-token': adminToken, // Use the custom header for admin token
                },
            });
            setTableData(response.data);
            setSelectedTable(tableName);
        } catch (error) {
            console.error('Error fetching table data:', error);
            // Optionally show an error message
        }
    };

    const handleUpdate = async () => {
        try {
            // Example: Update data for the selected table
            // Ensure adminToken is passed for authenticated API calls
            await axios.put(`/api/${selectedTable}`, { data: updateData }, {
                headers: {
                    'x-admin-token': adminToken, // Use the custom header for admin token
                },
            });
            // Optionally, you can refresh the table data after updating
            handleTableClick(selectedTable);
        } catch (error) {
            console.error('Error updating table data:', error);
         // Optionally show an error message
        }
    };

    // Display an unauthorized message if no admin token is found
    // This check should happen early in the render cycle
    // Display an unauthorized message if no admin token is found
    if (!adminToken) {
        return (
            <div className="admin-container unauthorized">
                <h1 className="admin-title">{t('admin_panel', { defaultValue: 'Admin Panel' })}</h1>
                <p>{t('admin_unauthorized_access', { defaultValue: 'Unauthorized access. Please log in as an administrator.' })}</p>
                {/* You might want a button to navigate to the admin login page */}
            </div>
        );
    }

    return (
        <div className="admin-container">
            <h1 className="admin-title">{t('admin_panel', { defaultValue: 'Admin Panel' })}</h1>

            {/* Coupon Creation Section */}
            <div className="admin-section">
                <h2 className="section-title">{t('create_coupon', { defaultValue: 'Create Coupon' })}</h2>
                {isLoading && <p>{t('loading', { defaultValue: 'Loading' })}...</p>}
                {couponMessage.text && (
                    <p className={`message ${couponMessage.type}`}>{couponMessage.text}</p>
                )}
                <div className="form-group">
                    <label htmlFor="gameSelect">{t('select_game', { defaultValue: 'Select Game' })}:</label>
                    <select
                        id="gameSelect"
                        value={selectedGame}
                        onChange={(e) => setSelectedGame(parseInt(e.target.value))}
                        disabled={isLoading}
                    >
                        {games.length === 0 && <option value="">{t('no_games_available', { defaultValue: 'No games available' })}</option>}
                        {games.map(game => (
                            <option key={game.id} value={game.id}>{game.title} ({game.city})</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="userSelect">{t('assign_to_user', { defaultValue: 'Assign to User (Optional)' })}:</label>
                    <select
                        id="userSelect"
                        value={selectedUser || ''}
                        onChange={(e) => setSelectedUser(e.target.value ? parseInt(e.target.value) : null)}
                        disabled={isLoading}
                    >
                        <option value="">{t('none_select_user', { defaultValue: 'None (General Coupon)' })}</option>
                        {users.map(user => (
                            <option key={user.user_id} value={user.user_id}>{user.username} ({user.email})</option>
                        ))}
                    </select>
                </div>
                
              
               
               
                <button onClick={handleCreateCoupon} disabled={isLoading} className="submit-button">
                    {t('create_coupon_button', { defaultValue: 'Create Coupon' })}
                </button>
            </div>

            {/* Surveys List Section */}
            <div className="admin-section">
                <h2 className="section-title">{t('all_surveys', { defaultValue: 'All Submitted Surveys' })}</h2>
                {isLoading && <p>{t('loading', { defaultValue: 'Loading' })}...</p>}
                {surveys.length > 0 ? (
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('survey_id', { defaultValue: 'ID' })}</th>
                                    <th>{t('game_title', { defaultValue: 'Game' })}</th>
                                    <th>{t('submitted_by', { defaultValue: 'Submitted By' })}</th>
                                    <th>{t('experience_rating', { defaultValue: 'Experience' })}</th>
                                    <th>{t('difficulty_rating', { defaultValue: 'Difficulty' })}</th>
                                    <th>{t('had_problem', { defaultValue: 'Had Problem' })}</th>
                                    <th>{t('problem_details', { defaultValue: 'Problem Details' })}</th>
                                    <th>{t('map_recommendations', { defaultValue: 'Map Recs' })}</th>
                                    <th>{t('general_feedback', { defaultValue: 'General Feedback' })}</th>
                                    <th>{t('learned_about', { defaultValue: 'Learned About' })}</th>
                                    <th>{t('submitted_at', { defaultValue: 'Submitted At' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {surveys.map((survey) => (
                                    <tr key={survey.survey_id}>
                                        <td>{survey.survey_id}</td>
                                        <td>{survey.game_title}</td>
                                        <td>{survey.submitted_by_username || survey.submitted_by_email || 'Guest'}</td>
                                        <td>{survey.experienceRating}</td>
                                        <td>{t(survey.difficultyRating, { defaultValue: survey.difficultyRating })}</td> {/* Translate difficulty */}
                                        <td>{survey.hadProblem ? t('Yes', { defaultValue: 'Yes' }) : t('No', { defaultValue: 'No' })}</td>
                                        <td>{survey.problemDetails || '-'}</td>
                                        <td>{t(survey.mapRecommendations, { defaultValue: survey.mapRecommendations }) || '-'}</td> {/* Translate map recs */}
                                        <td>{survey.feedbackComment || '-'}</td>
                                        <td>{t(survey.learnedAbout, { defaultValue: survey.learnedAbout }) || survey.learnedAboutOther || '-'}</td> {/* Translate learned about */}
                                        <td>{new Date(survey.submitted_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p>{t('no_surveys_found', { defaultValue: 'No surveys have been submitted yet.' })}</p>
                )}
            </div>
            {/* Existing Table View (optional - uncomment if you want to keep this functionality) */}
            {/*
            <div className="admin-section">
                <h2 className="section-title">{t('manage_data', { defaultValue: 'Manage Database Tables' })}</h2>
                <div className="menu">
                    <ul>
                        <li onClick={() => handleTableClick('coupons')} className={selectedTable === 'coupons' ? 'active-menu-item' : ''}>
                            {t('coupons_table', { defaultValue: 'Coupons' })}
                        </li>
                        <li onClick={() => handleTableClick('play_details')} className={selectedTable === 'play_details' ? 'active-menu-item' : ''}>
                            {t('play_details_table', { defaultValue: 'Play Details' })}
                        </li>
                        <li onClick={() => handleTableClick('team_game')} className={selectedTable === 'team_game' ? 'active-menu-item' : ''}>
                            {t('team_game_table', { defaultValue: 'Team Games' })}
                        </li>
                        {/* Add more tables as needed *//*}
                    </ul>
                </div>
                <div className="table-container">
                    {selectedTable && (
                        <div>
                            <h3>{t('data_for', { defaultValue: 'Data for', tableName: selectedTable.toUpperCase() })}</h3>
                            {tableData.length > 0 ? (
                                <table>
                                    <thead>
                                        <tr>
                                            {Object.keys(tableData[0]).map((key) => (
                                                <th key={key}>{key}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {Object.values(row).map((value, colIndex) => (
                                                    <td key={colIndex}>{String(value)}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p>{t('no_data_for_table', { defaultValue: 'No data available for this table.' })}</p>
                            )}
                            {/* Example for update (you'd need a more robust editing solution) *//*}
                            <div className="update-section">
                                <textarea
                                    placeholder={t('update_json_data', { defaultValue: 'Enter JSON to update selected row (e.g., {"id": 1, "column": "new_value"})' })}
                                    value={updateData}
                                    onChange={(e) => setUpdateData(e.target.value)}
                                    rows="5"
                                    cols="50"
                                />
                                <button onClick={handleUpdate}>{t('update_record', { defaultValue: 'Update Record' })}</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            */}
        </div>
    );
};

export default AuthContainer;